// Create a new component: SiteReviewPriorityPanel.js

import React, { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as AssignmentIcon,
  MonetizationOn as MoneyIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Warning as WarningIcon
} from '@material-ui/icons';
import moment from 'moment';
import {
  getPrioritizedReviewList,
  getMonthsSinceLastReview
} from './utils'

const useStyles = makeStyles(theme => ({
  priorityCard: {
    marginBottom: theme.spacing(2),
  },
  priorityChip: {
    fontWeight: 'bold',
    minWidth: 80,
  },
  criticalPriority: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  highPriority: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  mediumPriority: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
  lowPriority: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  minimalPriority: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  factorsList: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  siteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  scoreBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  revenueIcon: {
    color: theme.palette.success.main,
  },
  frequencyIcon: {
    color: theme.palette.info.main,
  },
  timeIcon: {
    color: theme.palette.warning.main,
  },
}));

const SiteReviewPriorityPanel = ({ 
  sites = [], 
  siteReviews = [], 
  onScheduleReview,
  maxDisplay = 10 
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState({});
  
  // Calculate prioritized list
  const prioritizedSites = useMemo(() => {
    return getPrioritizedReviewList(sites, siteReviews).slice(0, maxDisplay);
  }, [sites, siteReviews, maxDisplay]);

  const handleExpandClick = (siteId) => {
    setExpanded(prev => ({
      ...prev,
      [siteId]: !prev[siteId]
    }));
  };

  const getPriorityChipClass = (priority) => {
    switch (priority) {
      case 'CRITICAL': return classes.criticalPriority;
      case 'HIGH': return classes.highPriority;
      case 'MEDIUM': return classes.mediumPriority;
      case 'LOW': return classes.lowPriority;
      default: return classes.minimalPriority;
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'CRITICAL': return <WarningIcon />;
      case 'HIGH': return <AssignmentIcon />;
      case 'MEDIUM': return <ScheduleIcon />;
      default: return <StarIcon />;
    }
  };

  return (
    <Card className={classes.priorityCard} data-component="priority-panel">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Site Review Priority Queue
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Algorithmic prioritization based on revenue, frequency, customer type, and time
        </Typography>
        
        <List>
          {prioritizedSites.map((site, index) => {
            const priority = site.reviewPriority;
            const isExpanded = expanded[site._id];
            
            return (
              <React.Fragment key={site._id}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar style={{ backgroundColor: '#1976d2' }}>
                      {index + 1}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box className={classes.siteHeader}>
                        <Box>
                          <Typography variant="subtitle1" component="span">
                            {site.name || 'Unnamed Site'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {site.clientName} • {site.customerType}
                          </Typography>
                        </Box>
                        
                        <Box className={classes.scoreBox}>
                          <Chip
                            label={priority.priority}
                            className={`${classes.priorityChip} ${getPriorityChipClass(priority.priority)}`}
                            icon={getPriorityIcon(priority.priority)}
                            size="small"
                          />
                          <Typography variant="h6" color="primary">
                            {priority.totalScore}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {priority.recommendedAction}
                        </Typography>
                        
                        <Box display="flex" gap={1} mt={1} alignItems="center">
                          <Tooltip title="Monthly Revenue">
                            <Box display="flex" alignItems="center">
                              <MoneyIcon className={classes.revenueIcon} fontSize="small" />
                              <Typography variant="caption">
                                {site.monthlyValue ? `$${site.monthlyValue.toLocaleString()}/mo` : 'N/A'}
                              </Typography>
                            </Box>
                          </Tooltip>
                          
                          <Tooltip title="Service Frequency">
                            <Box display="flex" alignItems="center">
                              <ScheduleIcon className={classes.frequencyIcon} fontSize="small" />
                              <Typography variant="caption">
                                {site.frequency}
                              </Typography>
                            </Box>
                          </Tooltip>
                          
                          <Tooltip title="Last Review">
                            <Box display="flex" alignItems="center">
                              <AssignmentIcon className={classes.timeIcon} fontSize="small" />
                              <Typography variant="caption">
                                {getMonthsSinceLastReview(site, siteReviews) === null 
                                  ? 'Never' 
                                  : `${getMonthsSinceLastReview(site, siteReviews)}mo ago`}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>
                    }
                  />
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onScheduleReview && onScheduleReview(site)}
                    >
                      Schedule
                    </Button>
                    
                    <IconButton
                      size="small"
                      onClick={() => handleExpandClick(site._id)}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </ListItem>
                
                <Collapse in={isExpanded}>
                  <Box px={3} pb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Priority Calculation Factors:
                    </Typography>
                    <List dense className={classes.factorsList}>
                      {priority.factors.map((factor, idx) => (
                        <ListItem key={idx} style={{ paddingLeft: 0, paddingTop: 2, paddingBottom: 2 }}>
                          <Typography variant="caption">
                            • {factor}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Collapse>
                
                {index < prioritizedSites.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
        
        {prioritizedSites.length === 0 && (
          <Typography variant="body2" color="textSecondary" align="center" style={{ padding: 20 }}>
            No sites currently need review prioritization
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteReviewPriorityPanel;
